/**
 * Client-side shapes of the `cryptoOnchainPlatform.*` outputs, typed by hand
 * like the Apple Pay manage page does, so the components compile and test
 * before `@tagadapay/trpc-types` is rebuilt with the new router. Mirrors
 * `src/app/useCases/cryptoOnchainPlatform` DTOs; dates are ISO strings.
 */

/** The six chain families crypto-api settles on, in display order. */
export const PAYOUT_ADDRESS_KINDS = ['evm', 'solana', 'tron', 'btc', 'ltc', 'doge'] as const;
export type PayoutAddressKind = (typeof PAYOUT_ADDRESS_KINDS)[number];

/**
 * Kinds that are live; the others show as "coming soon" and cannot be edited. Mirrors
 * `CRYPTO_ONCHAIN_LIVE_PAYOUT_ADDRESS_KINDS` (src/lib/schemas/cryptoOnchain.ts).
 */
export const LIVE_PAYOUT_ADDRESS_KINDS: readonly PayoutAddressKind[] = ['evm', 'solana'];

/** A merchant's payout addresses: a missing kind means "not configured". */
export type PayoutAddresses = Partial<Record<PayoutAddressKind, string>>;

/** The write shape: `''` clears a kind, a missing kind is left untouched. */
export type PayoutAddressesInput = Partial<Record<PayoutAddressKind, string>>;

export const PAYMENT_STATUSES = [
  'pending',
  'detected',
  'paid',
  'paid_late',
  'underpaid',
  'overpaid',
  'expired',
  'failed',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export interface PlatformSettings {
  /** Env configured for this tenant (Tagada only). */
  configured: boolean;
  /** crypto-api answered the last settings read. */
  reachable: boolean;
  apiUrl: string | null;
  /** Percent (0–99.99, two decimals); crypto-api stores basis points. */
  defaultSharePercent: number;
  wallets: Record<PayoutAddressKind, string | null>;
  /** Platform wallets on the Bitcoin, Litecoin and Dogecoin testnets (sandbox stores). */
  testnetWallets: Record<'btc' | 'ltc' | 'doge', string | null>;
  error?: string;
}

/**
 * Testnets whose platform wallet staff set here: their addresses carry the network. Mirrors
 * `CRYPTO_ONCHAIN_SANDBOX_TESTNET_ADDRESS_KINDS` (src/lib/schemas/cryptoOnchain.ts); Dogecoin waits.
 */
export const TESTNET_WALLET_KINDS = ['btc', 'ltc'] as const;
export type TestnetWalletKind = (typeof TESTNET_WALLET_KINDS)[number];

export interface MerchantStats {
  paidSessions: number;
  sweptPayouts: number;
}

/**
 * The link row's lifecycle: `provisioning` while crypto-api is being called, `failed` when that call
 * did not go through (the row is kept; a retry reuses it), `active`, `disabled`.
 */
export const LINK_STATUSES = ['provisioning', 'active', 'disabled', 'failed'] as const;
export type LinkStatus = (typeof LINK_STATUSES)[number];

export interface PlatformMerchant {
  storeId: string;
  storeName: string;
  accountId: string;
  accountName: string | null;
  status: LinkStatus;
  cryptoApiMerchantId: string;
  /** Percent, null when the merchant follows the platform default. */
  sharePercentOverride: number | null;
  /** Percent actually applied at settlement. */
  effectiveSharePercent: number;
  payoutAddresses: PayoutAddresses;
  /** Sandbox mode (test networks only); null when crypto-api could not be reached. */
  sandbox: boolean | null;
  /** What the checkout offers now, in the merchant's mode; null when crypto-api could not be read. */
  offeredNetworks: OfferedNetwork[] | null;
  enabledAt: string | null;
  /** Null when crypto-api could not be reached. */
  stats: MerchantStats | null;
}

/** A network the merchant's checkout offers, with its coins. */
export interface OfferedNetwork {
  chainId: number;
  name: string;
  testnet: boolean;
  assets: string[];
}

/** The split crypto-api reported in `payout.settled`; amounts are base units. */
export interface Settlement {
  totalBase: string;
  merchantBase: string;
  platformBase: string | null;
  platformBps: number | null;
  sweepTxHash: string | null;
  platformTxHash: string | null;
  waivedReason: string | null;
}

export interface PlatformPayment {
  id: string;
  orderId: string;
  paymentId: string | null;
  status: PaymentStatus;
  chainId: number | null;
  chainName: string | null;
  asset: string | null;
  tokenAddress: string | null;
  amountBase: string | null;
  amountDecimals: number | null;
  /** `amountBase / 10^amountDecimals` as a decimal string. */
  amountFormatted: string | null;
  fiatAmount: string | null;
  fiatCurrency: string | null;
  originalAmount: string | null;
  originalCurrency: string | null;
  txHash: string | null;
  /** Block explorer link of `txHash`, from crypto-api. */
  explorerTxUrl: string | null;
  depositAddress: string | null;
  paymentUri: string | null;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  settlement: Settlement | null;
  /**
   * Paid after the order was already paid by another payment: recorded, never applied to the order,
   * owed back to the customer by hand (there is no refund path for crypto in this version).
   */
  duplicate: boolean;
  /** Paid on a testnet (sandbox mode): no real money moved. */
  test: boolean;
  /** A testnet payment that settled after the store left sandbox mode: the order was not paid. */
  rejected: boolean;
  storeName?: string | null;
}

/** A platform gas wallet: it pays the gas of token sweeps, one per chain family, read live per chain. */
export interface GasWallet {
  kind: string;
  chainId: number;
  chainName: string;
  /** A testnet (sandbox stores sweep tokens there): low only when empty. */
  testnet: boolean;
  address: string;
  nativeSymbol: string;
  /** Formatted native balance; null when the read failed. */
  balance: string | null;
  lowBalance: boolean;
  error: string | null;
}

/** One chain crypto-api offers, read live with its own credentials: can a payment there be detected now. */
export interface ChainHealth {
  chainId: number;
  name: string;
  testnet: boolean;
  /** `alchemy`, `trongrid` or `esplora`. */
  provider: string;
  /** Host and path of the reads, without credentials. */
  endpoint: string;
  ok: boolean;
  latencyMs: number;
  /** The provider's message when a read failed. */
  error: string | null;
}

export const PAYOUT_STAGES = [
  'pending',
  'sweeping',
  'gas_funded',
  'platform_paid',
  'broadcasting',
  'swept',
  'failed',
  'skipped',
] as const;
export type PayoutStage = (typeof PAYOUT_STAGES)[number];

/** One sweep of a paid session: what reached the merchant and what reached the platform wallet. */
export interface PlatformPayout {
  id: string;
  sessionId: string;
  cryptoOnchainPaymentId: string | null;
  storeId: string | null;
  storeName: string | null;
  chainId: number;
  chainName: string;
  asset: string;
  stage: string;
  amount: string | null;
  merchantAmount: string | null;
  platform: {
    address: string | null;
    bps: number | null;
    amount: string | null;
    txHash: string | null;
    waivedReason: string | null;
  } | null;
  destinationAddress: string | null;
  sweepTxHash: string | null;
  explorerTxUrl: string | null;
  error: string | null;
  attempts: number;
  createdAt: string;
  completedAt: string | null;
}
